// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { tokens } from '../design-system/tokens';
import type { ChatRoomShellOverlayHandlers } from './useChatRoomShellViewModel';

/**
 * Cycle 16 Wave 3 WEB-WIRE-B — additive shell around `<ChatRoom />`.
 *
 * Mounts the chat layout in a row container and exposes named slots
 * (`hosts.thread`, `hosts.pollComposer`, …) so per-app hosts can wire
 * their own modal stack and right-pane thread panel into the shared UI.
 * The shell itself never decides what is visible — it just renders the
 * React node the host passes in.
 *
 * The shell does not own any state; the caller passes a fully resolved
 * `handlers` object (typically from
 * {@link useChatRoomShellViewModel}). This keeps the shell consumable
 * from both `react-native-web` and bare `react-native-macos` without
 * the shell knowing which app is rendering it.
 *
 * No SDK access, no IO, no `console.*`. The component is pure.
 */

/**
 * Named host slots. When a slot is `undefined` (or `null`) the
 * corresponding overlay is silently elided; the matching `open*`
 * handler still records the intent in the view-model state but the
 * shell renders nothing. Per-app hosts decide which surfaces they
 * support (e.g. desktop v1 may skip `settings`).
 */
export interface ChatRoomShellHosts {
  readonly thread?: React.ReactNode;
  readonly pollComposer?: React.ReactNode;
  readonly imageEditor?: React.ReactNode;
  readonly shareLocation?: React.ReactNode;
  readonly setStatus?: React.ReactNode;
  readonly settings?: React.ReactNode;
  readonly miniProfile?: React.ReactNode;
  readonly userProfile?: React.ReactNode;
}

export interface ChatRoomShellProps {
  /**
   * Retained for API compatibility — callers passing the active
   * conversation id can keep doing so even though the shell itself
   * no longer renders a chat surface internally. Hosts pass their
   * own chat root (typically `<ChatRoom>` via `WebChatRoom`)
   * through `children`.
   */
  readonly conversationId: string;
  readonly hosts: ChatRoomShellHosts;
  readonly handlers: ChatRoomShellOverlayHandlers;
  /** True when the right-pane thread panel is visible. Drives layout. */
  readonly threadActive: boolean;
  /**
   * The chat surface to mount in the shell's main pane. Hosts pass
   * their own chat tree here — `<WebChatRoom>` wrapping `ChatRoom`
   * on web and desktop. If omitted the shell renders nothing in the
   * main pane.
   */
  readonly children?: React.ReactNode;
}

const ChatRoomShellHandlersContext = createContext<ChatRoomShellOverlayHandlers | null>(null);

/**
 * Hook for descendants (composer, message rows) to access the shell's
 * overlay handlers without prop-drilling. Returns `null` when the
 * descendant is rendered outside a `ChatRoomShell` — callers must
 * tolerate this so the existing `<ChatRoom />` consumers (which do
 * not wrap in a shell) keep compiling.
 */
export function useChatRoomShellHandlers(): ChatRoomShellOverlayHandlers | null {
  return useContext(ChatRoomShellHandlersContext);
}

const THREAD_PANEL_WIDTH = 380;

/**
 * Provider-only export — `ChatRoomShell` already wraps with this
 * internally. Exposed so tests can mount the message row adapter in
 * isolation while still exercising the handlers context path.
 */
export const ChatRoomShellHandlersProvider: React.FC<{
  readonly handlers: ChatRoomShellOverlayHandlers;
  readonly children: React.ReactNode;
}> = ({ handlers, children }) => (
  <ChatRoomShellHandlersContext.Provider value={handlers}>
    {children}
  </ChatRoomShellHandlersContext.Provider>
);

export const ChatRoomShell: React.FC<ChatRoomShellProps> = ({
  hosts,
  handlers,
  threadActive,
  children,
}) => {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const semantic = tokens.color.semantic[colorScheme];

  const layoutStyle = useMemo<ViewStyle[]>(() => {
    // Web + macOS render the thread panel side-by-side; native targets
    // that consume this shell from other platforms would degrade to a
    // stack via `Platform.select`. We do not support the shell on
    // non-web platforms today, but the conditional keeps render safe.
    if (Platform.OS === 'web' || Platform.OS === 'macos') {
      return [styles.row];
    }
    return [styles.column];
  }, []);

  const threadPaneStyle = useMemo<ViewStyle[]>(
    () => [styles.threadPane, { borderLeftColor: semantic.border.secondary }],
    [semantic.border.secondary],
  );

  // Hosts (`apps/web` + `apps/desktop`) always pass `children` (their
  // `WebChatRoom` wrapping `ChatRoom`). The shell renders whatever
  // they supply with no fallback.
  const mainPane = children ?? null;

  return (
    <ChatRoomShellHandlersContext.Provider value={handlers}>
      <View style={layoutStyle} testID="chatroom-shell">
        <View style={styles.mainPane}>{mainPane}</View>
        {threadActive && hosts.thread !== undefined && hosts.thread !== null ? (
          <View style={threadPaneStyle} testID="chatroom-shell-thread-pane">
            {hosts.thread}
          </View>
        ) : null}
      </View>
      {/*
        Modal stack slots render outside the row layout. Each per-app host
        owns its own portal / Modal mounting strategy; the shell renders
        the host node unconditionally so the host can present / dismiss
        without remounting the chat surface. `null` slots are no-ops.
      */}
      {hosts.pollComposer}
      {hosts.imageEditor}
      {hosts.shareLocation}
      {hosts.setStatus}
      {hosts.settings}
      {hosts.miniProfile}
      {hosts.userProfile}
    </ChatRoomShellHandlersContext.Provider>
  );
};

const styles = StyleSheet.create({
  row: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: 'transparent',
  } satisfies ViewStyle,
  column: {
    flex: 1,
    flexDirection: 'column',
  } satisfies ViewStyle,
  mainPane: {
    flex: 1,
    minWidth: 0,
  } satisfies ViewStyle,
  threadPane: {
    width: THREAD_PANEL_WIDTH,
    minWidth: THREAD_PANEL_WIDTH,
    borderLeftWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
});
